import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Archive, CalendarClock, Edit, Share2 } from "lucide-react";
import {
  OUTFIT_ROLE_ORDER, Outfit, OutfitItemThumb, OutfitRole,
  fileUrl, outfitsApi,
} from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { ShareDialog } from "@/components/catalog/ShareDialog";
import { toast } from "sonner";

export default function OutfitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [outfit, setOutfit] = useState<Outfit | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    outfitsApi.get(id)
      .then(setOutfit)
      .catch((e) => toast.error(e?.message || "Outfit not found"))
      .finally(() => setLoading(false));
  }, [id]);

  async function archive() {
    if (!outfit) return;
    if (!window.confirm("Archive this outfit? You can find archived outfits later.")) return;
    try {
      await outfitsApi.archive(outfit._id);
      toast.success("Outfit archived.");
      navigate("/app/outfits");
    } catch (e: any) {
      toast.error(e?.message || "Could not archive.");
    }
  }

  if (loading) {
    return (
      <main className="flex-1 px-6 md:px-10 py-10" data-testid="outfit-detail-loading">
        <div className="h-7 w-40 bg-bone animate-pulse mb-4" />
        <div className="h-12 w-2/3 bg-bone animate-pulse" />
      </main>
    );
  }
  if (!outfit) return null;

  const occasionLine = [
    outfit.occasion,
    outfit.scheduled_for && new Date(outfit.scheduled_for).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }),
  ].filter(Boolean).join(" · ");

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="outfit-detail-page">
      <button type="button" onClick={() => navigate("/app/outfits")}
        data-testid="outfit-detail-back"
        className="text-xs text-muted-foreground hover:text-oxblood inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to outfits
      </button>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Lookbook</p>
          <h1 className="font-display text-3xl sm:text-4xl text-balance" data-testid="outfit-detail-name">{outfit.name}</h1>
          {occasionLine && (
            <p className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
              <CalendarClock className="w-4 h-4" /> {occasionLine}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="outfit-share-btn"
            onClick={() => setShareOpen(true)}
            className="rounded-none border-foreground/20 hover:bg-bone">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <Button asChild variant="outline" data-testid="outfit-edit-btn"
            className="rounded-none border-foreground/20 hover:bg-bone">
            <Link to={`/app/outfits/${outfit._id}/edit`}><Edit className="w-4 h-4 mr-2" /> Edit</Link>
          </Button>
          <Button variant="outline" onClick={archive}
            data-testid="outfit-archive-btn"
            className="rounded-none border-foreground/20 hover:bg-bone text-oxblood">
            <Archive className="w-4 h-4 mr-2" /> Archive
          </Button>
        </div>
      </div>

      {outfit.description && (
        <p className="mt-6 max-w-prose text-sm leading-relaxed text-foreground/80" data-testid="outfit-description">
          {outfit.description}
        </p>
      )}

      <RoleGroupedItems outfit={outfit} />

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} kind="outfit" targetId={outfit._id} title={outfit.name} />
    </main>
  );
}

function RoleGroupedItems({ outfit }: { outfit: Outfit }) {
  const grouped = useMemo(() => {
    const buckets = new Map<OutfitRole, OutfitItemThumb[]>();
    for (const it of outfit.items) {
      const role = it.role || "Other";
      if (!buckets.has(role)) buckets.set(role, []);
      buckets.get(role)!.push(it);
    }
    return OUTFIT_ROLE_ORDER
      .map((role) => ({ role, items: buckets.get(role) || [] }))
      .filter((g) => g.items.length > 0);
  }, [outfit.items]);

  if (grouped.length === 0) {
    return (
      <p className="mt-12 text-sm text-muted-foreground" data-testid="outfit-items-empty">
        This outfit doesn't have any items yet.
      </p>
    );
  }

  return (
    <div className="mt-12 space-y-12" data-testid="outfit-items-grid">
      {grouped.map(({ role, items }) => (
        <section key={role} data-testid={`outfit-role-section-${role}`}>
          <header className="mb-4 flex items-baseline gap-3">
            <h3 className="font-display text-xl tracking-tight">
              {role}
              <span className="ml-2 text-xs text-muted-foreground font-sans tracking-normal">
                {items.length} {items.length === 1 ? "piece" : "pieces"}
              </span>
            </h3>
            <span className="flex-1 h-px bg-border relative">
              <span className="absolute left-0 top-0 h-px w-12 bg-oxblood" />
            </span>
          </header>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => (
              <Link key={it.item_id} to={`/app/items/${it.item_id}`}
                data-testid={`outfit-item-${it.item_id}`}
                className="group block bg-background border border-border hover:border-oxblood/40">
                <div className="aspect-[4/5] bg-bone overflow-hidden">
                  {it.primary_photo_url ? (
                    <img src={fileUrl(it.primary_photo_url)} alt={it.name} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                  ) : <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">No photo</div>}
                </div>
                <div className="p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">{it.brand || it.category}</p>
                  <p className="font-display text-sm leading-snug line-clamp-2 mt-1">{it.name}</p>
                  {it.notes && (
                    <p className="text-[11px] text-muted-foreground mt-1 italic line-clamp-1">{it.notes}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
